import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * One button, four intents.
 *
 * `busy` is separate from `disabled` because they mean different things to a
 * screen reader: disabled says "not available", busy says "working on it". A busy
 * button stays focusable and announces its state through `aria-busy`.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  busy?: boolean;
  /** What to say while `busy`, so the label does not simply vanish. */
  busyLabel?: string;
  block?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'secondary',
  busy = false,
  busyLabel,
  block = false,
  className,
  children,
  type = 'button',
  disabled,
  ...rest
}: ButtonProps): ReactNode {
  const classes = ['button', `button-${variant}`, block ? 'button-block' : '', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled === true || busy}
      data-busy={busy ? 'true' : undefined}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy && <span className="spinner" aria-hidden="true" />}
      {busy && busyLabel !== undefined ? busyLabel : children}
    </button>
  );
}
